/**
 * The additional-options section at the foot of the booked-shipment page.
 *
 * Each option resolves to one of three things: a dialog we own (change pickup
 * date, modify), a handoff to Intercom for the ones we can't do ourselves yet
 * (cancel, and modify unless the hidden modify experience is switched on), or a
 * navigation (submit a claim, repeat this shipment).
 */

import { useSavedLocationsApi } from "@/Hooks/useSavedLocationsApi";
import type { Shipment } from "@freightsimple/generated-dashboard-openapi-client/models/Shipment";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useIntercom } from "react-use-intercom";
import { nextBusinessDay } from "@/Shared/Dates/nextBusinessDay";
import { loadFromShipmentToModify } from "@/Slices/bookShipmentSlice";
import { ModifyShipmentDialog } from "@/Features/ModifyShipment/Exports/Dialogs/ModifyShipmentDialog";
import ViewShipmentsAdditionalOptions from "../CustomComponents/ViewShipmentsAdditionalOptions";
import { useViewShipmentsChangePickupDateDialog } from "../Hooks/useViewShipmentsChangePickupDateDialog";
import { useViewShipmentsConfirmThenWriteIntercomMessage } from "../Hooks/useViewShipmentsConfirmThenWriteIntercomMessage";
import { viewShipmentsPrepareShipmentForRepeat } from "../Helpers/viewShipmentsPrepareShipmentForRepeat";

interface ViewShipmentsAdditionalOptionsForShipmentProps {
  shipment: Shipment;
  onReloadShipment: () => Promise<void>;
}

export function ViewShipmentsAdditionalOptionsForShipment(
  props: ViewShipmentsAdditionalOptionsForShipmentProps,
) {
  const [modifyShipmentExperienceEnabled, setModifyShipmentExperience] =
    useState(false);
  useHotkeys("ctrl+m", () =>
    setModifyShipmentExperience(!modifyShipmentExperienceEnabled),
  );

  const { shipment, onReloadShipment } = props;
  const { show } = useIntercom();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const savedLocationsApi = useSavedLocationsApi();
  const [modifyShipmentOpen, setModifyShipmentOpen] = useState(false);
  const changePickupDateDialog = useViewShipmentsChangePickupDateDialog(
    shipment,
    onReloadShipment,
  );

  const onCancel = useViewShipmentsConfirmThenWriteIntercomMessage(
    "Hello! I would like to cancel this shipment.",
    "Cancelling this shipment will require the assistance of FreightSimple Customer Support. Would you like to continue?",
  );

  const onChangePickupDateViaIntercom =
    useViewShipmentsConfirmThenWriteIntercomMessage(
      "Hello! I would like to change the pickup date or hours for this shipment.",
      "Changing the pickup date or hours for this shipment will require the assistance of FreightSimple Customer Support. Would you like to continue?",
    );

  const onModifyViaIntercom = useViewShipmentsConfirmThenWriteIntercomMessage(
    "Hello! I would like to modify this shipment.",
    "Modifying this shipment will require the assistance of FreightSimple Customer Support. However, this is not always possible. Would you like to continue?",
  );

  function onModify() {
    if (modifyShipmentExperienceEnabled) {
      setModifyShipmentOpen(true);
    } else {
      onModifyViaIntercom.trigger();
    }
  }

  function onSubmitClaim() {
    navigate(`/submit-claim?shipmentId=${props.shipment.shipmentId}`);
  }

  async function handleChangePickupDate() {
    if (shipment.shipmentState === ShipmentState.OnHold) {
      onChangePickupDateViaIntercom.trigger();
    } else {
      changePickupDateDialog.show();
    }
  }

  async function onRepeatShipment() {
    const response = await savedLocationsApi.getAllSavedLocations();
    const savedLocations = response.items;
    const newPickupDate = nextBusinessDay().format("YYYY-MM-DD");
    dispatch(
      loadFromShipmentToModify({
        shipment: viewShipmentsPrepareShipmentForRepeat(
          shipment,
          newPickupDate,
        ),
        savedLocations,
      }),
    );
    navigate("/book/details");
  }

  return (
    <>
      {changePickupDateDialog.dialogs}
      <ModifyShipmentDialog
        open={modifyShipmentOpen}
        onOpenChange={setModifyShipmentOpen}
        shipment={shipment}
      />
      {onCancel.dialogs}
      {onChangePickupDateViaIntercom.dialogs}
      {onModifyViaIntercom.dialogs}
      <ViewShipmentsAdditionalOptions
        onLiveChat={() => show()}
        onChangePickupDate={handleChangePickupDate}
        onHelpCenter={() => window.open("https://help.freightsimple.com")}
        onCancelShipment={onCancel.trigger}
        onModifyShipment={onModify}
        onRepeatShipment={onRepeatShipment}
        onSubmitClaim={onSubmitClaim}
        shipment={shipment}
      />
    </>
  );
}
